import { hasHighlightSegments } from './taggedTextParser';

/**
 * types.ts — Shared types for the web application.
 *
 * This file defines the content block types and message history structures
 * used across the web application. These types mirror the jonas_agent types
 * to ensure consistency between frontend and backend.
 */

// =============================================================================
// CONTENT TYPE ENUM
// =============================================================================

/**
 * Enum representing the types of content available in the system.
 * Used to categorize jokes, projects, and testimonials.
 */
export enum ContentType {
    PROJECT = 'PROJECT',
    TESTIMONIAL = 'TESTIMONIAL',
    JOKE = 'JOKE',
}

// =============================================================================
// CONTENT BLOCK INTERFACES
// =============================================================================

/**
 * A simple text content block for user messages.
 */
export interface SimpleTextContentBlock {
    type: 'SIMPLE_TEXT';
    text: string;
}

/**
 * A UI message content block for assistant messages displayed to the user.
 * These messages are NOT sent back to the jonas_agent.
 */
export interface UiMessageContentBlock {
    type: 'UI_MESSAGE';
    text: string;
}

/**
 * An error content block indicating a failure occurred.
 */
export interface ErrorContentBlock {
    type: 'ERROR';
    error: 'INTERNAL_ERROR' | 'TIMEOUT';
}

/**
 * A content block indicating no matching content was found.
 */
export interface NotFoundContentBlock {
    type: 'NOT_FOUND';
}

/**
 * A content block containing only the type and id of a content item.
 * Used for referencing content without including full details.
 */
export interface IdContentBlock {
    type: ContentType;
    id: string;
}

/**
 * A content block indicating the user accepted an NBA suggestion.
 */
export interface AcceptedNextBestActionContentBlock {
    type: 'ACCEPTED_NEXT_BEST_ACTION';
    nba: AcceptableNextBestAction;
}

/**
 * A content block representing an NBA suggestion in the assistant's turn.
 */
export interface NextBestActionContentBlock {
    type: 'NEXT_BEST_ACTION';
    nba: AcceptableNextBestAction;
}

/**
 * A loading content block indicating content is being fetched.
 */
export interface LoadingContentBlock {
    type: 'LOADING';
}

export interface Company {
    name: string;
    logo: string;
}

/**
 * Full project content block with all project details.
 */
export interface FullProjectContentBlock {
    type: 'PROJECT';
    id: string;
    start: {
        month: number;
        year: number;
    };
    end: {
        month: number;
        year: number;
    };
    title: string;
    company: Company;
    focusDescription: string;
    bulletPoints: string[];
    tags: string[];
}

/**
 * Full testimonial content block with all testimonial details.
 */
export interface FullTestimonialContentBlock {
    type: 'TESTIMONIAL';
    id: string;
    person: {
        name: string;
        role: string;
        image: string | null;
    };
    company: string;
    text: string;
    relatedProjectId: string | null;
    tags: string[];
}

/**
 * Full joke content block with all joke details.
 */
export interface FullJokeContentBlock {
    type: 'JOKE';
    id: string;
    joke: string;
    tags: string[];
}

/**
 * Union type for all full content blocks.
 */
export type FullContentBlock = FullProjectContentBlock | FullTestimonialContentBlock | FullJokeContentBlock;

/**
 * Union type for all renderable content blocks.
 */
export type RenderableContentBlock = 
    | SimpleTextContentBlock 
    | UiMessageContentBlock 
    | ErrorContentBlock 
    | NotFoundContentBlock 
    | FullContentBlock
    | NextBestActionContentBlock
    | AcceptedNextBestActionContentBlock;

/**
 * The two participant roles supported by the chat turn model.
 */
export type ParticipantRole = "assistant" | "user";

// =============================================================================
// UI MESSAGE HISTORY TYPES
// =============================================================================

/**
 * A message history item for the UI.
 * - USER messages contain SimpleTextContentBlock or AcceptedNextBestActionContentBlock
 * - ASSISTANT messages can contain various content block types or an array of full content blocks, and optional NBAs
 */
export type UiMessageHistoryItem =
    | { role: 'USER'; contentBlock: SimpleTextContentBlock | AcceptedNextBestActionContentBlock }
    | { role: 'ASSISTANT'; contentBlock: UiMessageContentBlock | ErrorContentBlock | NotFoundContentBlock; nbas?: AcceptableNextBestAction[] }
    | { role: 'ASSISTANT'; contentBlocks: FullContentBlock[]; nbas?: AcceptableNextBestAction[] };

// =============================================================================
// JONAS AGENT REQUEST TYPES
// =============================================================================

/**
 * Basic ID and text for an NBA in backend history.
 */
export interface NextBestActionIdAndText {
    id: string;
    text: string;
}

/**
 * A message history item in the jonas_agent request.
 * - USER messages contain SimpleTextContentBlock or AcceptedNextBestActionContentBlock
 * - ASSISTANT messages contain either NotFoundContentBlock or IdContentBlock[]
 * 
 * Note: UiMessageContentBlock and ErrorContentBlock are NOT sent to the backend.
 */
export type JonasAgentRequestMessageHistoryItem =
    | { role: 'USER'; contentBlock: SimpleTextContentBlock | AcceptedNextBestActionContentBlock }
    | { role: 'ASSISTANT'; contentBlock: NotFoundContentBlock; nbas?: AcceptableNextBestAction[] }
    | { role: 'ASSISTANT'; contentBlocks: IdContentBlock[]; nbas?: AcceptableNextBestAction[] };

/**
 * The request body for the jonas_agent API.
 */
export interface JonasAgentRequestBody {
    history: JonasAgentRequestMessageHistoryItem[];
}

export interface AcceptableNextBestAction {
    id: string;
    text: string;
    previouslyShownContentBlockId: {
        type: ContentType;
        id: string;
    } | {
        type: 'NOT_FOUND';
    }
}

/**
 * A successful response containing one or more full content blocks.
 */
export interface SuccessContentListBlock {
    type: 'SUCCESS';
    blocks: FullContentBlock[];
}

/**
 * The response body from the jonas_agent API.
 */
export interface JonasAgentResponseBody {
    content: ErrorContentBlock | NotFoundContentBlock | SuccessContentListBlock;
    nbas: AcceptableNextBestAction[];
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a content block is an ErrorContentBlock.
 */
export function isErrorContentBlock(block: unknown): block is ErrorContentBlock {
    if (!block || typeof block !== 'object') {
        return false;
    }
    const b = block as Record<string, unknown>;
    return b.type === 'ERROR' && (b.error === 'INTERNAL_ERROR' || b.error === 'TIMEOUT');
}

/**
 * Type guard to check if a content block is a NotFoundContentBlock.
 */
export function isNotFoundContentBlock(block: unknown): block is NotFoundContentBlock {
    if (!block || typeof block !== 'object') {
        return false;
    }
    const b = block as Record<string, unknown>;
    return b.type === 'NOT_FOUND';
}

/**
 * Type guard to check if a content block is an IdContentBlock.
 */
export function isIdContentBlock(block: unknown): block is IdContentBlock {
    if (!block || typeof block !== 'object') {
        return false;
    }
    const b = block as Record<string, unknown>;
    return (
        (b.type === ContentType.PROJECT || b.type === ContentType.TESTIMONIAL || b.type === ContentType.JOKE) &&
        typeof b.id === 'string'
    );
}

/**
 * Type guard to check if an object is an AcceptableNextBestAction.
 */
export function isAcceptableNextBestAction(data: unknown): data is AcceptableNextBestAction {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const d = data as Record<string, unknown>;
    if (typeof d.id !== 'string' || typeof d.text !== 'string' || !d.previouslyShownContentBlockId || typeof d.previouslyShownContentBlockId !== 'object') {
        return false;
    }
    const prev = d.previouslyShownContentBlockId as Record<string, unknown>;
    return (
        (prev.type === 'NOT_FOUND') ||
        ((prev.type === ContentType.PROJECT || prev.type === ContentType.TESTIMONIAL || prev.type === ContentType.JOKE) && typeof prev.id === 'string')
    );
}

/**
 * Type guard to check if a content block is a FullProjectContentBlock.
 */
export function isFullProjectContentBlock(block: unknown): block is FullProjectContentBlock {
    if (!block || typeof block !== 'object') {
        return false;
    }
    const b = block as Record<string, unknown>;
    if (b.type !== 'PROJECT' || typeof b.id !== 'string' || typeof b.title !== 'string') {
        return false;
    }
    
    // Check company object
    if (!b.company || typeof b.company !== 'object') {
        return false;
    }
    const company = b.company as Record<string, unknown>;
    return typeof company.name === 'string' && typeof company.logo === 'string';
}

/**
 * Type guard to check if a content block is a FullTestimonialContentBlock.
 */
export function isFullTestimonialContentBlock(block: unknown): block is FullTestimonialContentBlock {
    if (!block || typeof block !== 'object') {
        return false;
    }
    const b = block as Record<string, unknown>;
    if (
        b.type !== 'TESTIMONIAL' ||
        typeof b.id !== 'string' ||
        typeof b.text !== 'string' ||
        'highlight' in b ||
        !hasHighlightSegments(b.text) ||
        typeof b.company !== 'string' ||
        !Array.isArray(b.tags) ||
        !b.person ||
        typeof b.person !== 'object'
    ) {
        return false;
    }

    const person = b.person as Record<string, unknown>;
    return (
        typeof person.name === 'string' &&
        typeof person.role === 'string' &&
        (person.image === null || typeof person.image === 'string') &&
        (b.relatedProjectId === null || b.relatedProjectId === undefined || typeof b.relatedProjectId === 'string')
    );
}

/**
 * Type guard to check if a content block is a FullJokeContentBlock.
 */
export function isFullJokeContentBlock(block: unknown): block is FullJokeContentBlock {
    if (!block || typeof block !== 'object') {
        return false;
    }
    const b = block as Record<string, unknown>;
    return b.type === 'JOKE' && typeof b.id === 'string' && typeof b.joke === 'string';
}

/**
 * Type guard to check if a content block is any FullContentBlock.
 */
export function isFullContentBlock(block: unknown): block is FullContentBlock {
    return isFullProjectContentBlock(block) || isFullTestimonialContentBlock(block) || isFullJokeContentBlock(block);
}

/**
 * Type guard to check if a response is a SuccessContentListBlock.
 */
export function isSuccessContentListBlock(data: unknown): data is SuccessContentListBlock {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const d = data as Record<string, unknown>;
    return d.type === 'SUCCESS' && Array.isArray(d.blocks) && d.blocks.every(isFullContentBlock);
}

/**
 * Type guard to check if a response is a valid JonasAgentResponseBody.
 */
export function isJonasAgentResponseBody(data: unknown): data is JonasAgentResponseBody {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const d = data as Record<string, unknown>;
    return (
        'content' in d &&
        (isErrorContentBlock(d.content) || isNotFoundContentBlock(d.content) || isSuccessContentListBlock(d.content)) &&
        'nbas' in d &&
        Array.isArray(d.nbas) &&
        d.nbas.every(isAcceptableNextBestAction)
    );
}
